import {useRef, useState, useEffect, useCallback, useMemo} from 'react'
import {useTheme} from '@chakra-ui/react'

const BLEED_THRESHOLD = 50 // px - apply bleed effect when container margin is less than this
const FULL_WIDTH_THRESHOLD = 10 // px tolerance for measurement

// Default offsets when container is already full viewport (no container margins to measure)
const DEFAULT_MOBILE_OFFSET = 16
const DEFAULT_TABLET_OFFSET = 16
const DEFAULT_DESKTOP_OFFSET = 24

/**
 * Convert Chakra breakpoint value (e.g., "48em") to pixels
 */
const breakpointToPx = (breakpoint) => {
    if (typeof breakpoint === 'number') return breakpoint
    if (typeof breakpoint === 'string') {
        if (breakpoint.endsWith('em')) {
            return parseFloat(breakpoint) * 16
        }
        if (breakpoint.endsWith('px')) {
            return parseFloat(breakpoint)
        }
    }
    return 0
}

/**
 * Hook to determine if carousel should apply bleed effect
 *
 * Calculates whether the carousel has enough items to scroll at each breakpoint,
 * and returns bleed configuration including offset values for Swiper.
 * Bleed is only applied when:
 * 1. The carousel is scrollable (more items than visible slides)
 * 2. The container margin is less than BLEED_THRESHOLD (close to viewport edge)
 *
 * @param {Object} options
 * @param {number} options.itemCount - Number of items in the carousel
 * @param {number} options.slidesToShowMobile - Slides visible on mobile (e.g., 2.3)
 * @param {number} options.slidesToShowTablet - Slides visible on tablet (e.g., 3.5)
 * @param {number} options.slidesToShowDesktop - Slides visible on desktop (e.g., 4.5)
 * @returns {Object} { containerRef, shouldBleedMobile, shouldBleedDesktop, mobileOffset, desktopOffset, bleedMargin, isScrollableDesktop }
 */
const useCarouselBleed = ({
    itemCount = 0,
    slidesToShowMobile = 2.3,
    slidesToShowTablet = 3.5,
    slidesToShowDesktop = 4.5,
    remeasureTrigger = 0 // Increment this to force re-measurement (e.g., after viewport hydration)
}) => {
    const theme = useTheme()
    const containerRef = useRef(null)
    // Track two states:
    // - isFullWidth: container is close to viewport edge (eligible for bleed)
    // - isAlreadyFullViewport: container is exactly viewport width (no negative margins needed)
    const [isFullWidth, setIsFullWidth] = useState(true) // Default true for SSR
    const [isAlreadyFullViewport, setIsAlreadyFullViewport] = useState(false)
    const [viewportWidth, setViewportWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 375
    )
    // Dynamic margin measurement - the actual distance from container to viewport edge
    const [measuredMargin, setMeasuredMargin] = useState(16) // Default fallback

    // Get breakpoints from theme (converts "48em" -> 768, "62em" -> 992, etc.)
    const breakpoints = useMemo(
        () => ({
            md: breakpointToPx(theme.breakpoints.md),
            lg: breakpointToPx(theme.breakpoints.lg)
        }),
        [theme.breakpoints.md, theme.breakpoints.lg]
    )

    const checkFullWidth = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            const clientWidth = document.documentElement.clientWidth

            // Measure the left margin (distance from container to viewport edge)
            const leftMargin = Math.round(rect.left)

            // Check if container matches or exceeds client width (already full bleed)
            const matchesViewport =
                Math.abs(clientWidth - rect.width) <= FULL_WIDTH_THRESHOLD ||
                rect.width >= clientWidth

            // Check if container is close to edge (within threshold)
            const isCloseToEdge = leftMargin < BLEED_THRESHOLD

            setIsFullWidth(matchesViewport || isCloseToEdge)
            setIsAlreadyFullViewport(matchesViewport)
            setViewportWidth(clientWidth)
            setMeasuredMargin(leftMargin > 0 ? leftMargin : 16) // Store measured margin, min 16px
        }
    }, [])

    // Measure container vs viewport width on client side
    // Re-run when itemCount changes (indicates new page/data)
    // Re-run when remeasureTrigger changes (e.g., after viewport hydration)
    useEffect(() => {
        // Reset to true initially, then measure after layout
        setIsFullWidth(true)

        // Use requestAnimationFrame to ensure DOM is painted before measuring
        const rafId = requestAnimationFrame(() => {
            checkFullWidth()
        })

        window.addEventListener('resize', checkFullWidth)
        return () => {
            cancelAnimationFrame(rafId)
            window.removeEventListener('resize', checkFullWidth)
        }
    }, [itemCount, remeasureTrigger, checkFullWidth])

    // Determine if carousel is scrollable at each breakpoint
    // Use >= to handle fractional slides (e.g., 4.5 slides showing 4 full + partial)
    const isScrollableMobile = itemCount > slidesToShowMobile
    const isScrollableTablet = itemCount > slidesToShowTablet
    const isScrollableDesktop = itemCount > slidesToShowDesktop

    // Check if we're on desktop
    const isDesktop = typeof window !== 'undefined' ? window.innerWidth >= breakpoints.lg : true

    // Determine if item count exactly matches the configured slots (no bleed needed)
    // Only disable bleed when items exactly match the unaltered slidesToShow value
    // e.g., set to 5, received 5 = no bleed (exact match)
    // e.g., set to 4.5, received 5 = bleed (creates carousel with partial tile visible)
    // e.g., set to 2.5, received 2 = bleed (fewer items than slots)
    const isExactMatchMobile = itemCount === slidesToShowMobile
    const isExactMatchTablet = itemCount === slidesToShowTablet
    const isExactMatchDesktop = itemCount === slidesToShowDesktop

    // Calculate bleed conditions:
    // - Bleed when NOT an exact match (either scrollable OR fewer items than slots)
    // - This ensures consistent tile sizing across carousels with different item counts
    const shouldBleedMobile = !isExactMatchMobile && isFullWidth
    const shouldBleedTablet = !isExactMatchTablet && isFullWidth
    const shouldBleedDesktop = !isExactMatchDesktop && isDesktop && isFullWidth

    // Determine the effective offset value for each breakpoint:
    // - When already full viewport: use defaults (container has no margins to measure)
    // - When within threshold: use measured margin (container has small margins we need to compensate for)
    const mobileEffectiveOffset = isAlreadyFullViewport ? DEFAULT_MOBILE_OFFSET : measuredMargin
    const tabletEffectiveOffset = isAlreadyFullViewport ? DEFAULT_TABLET_OFFSET : measuredMargin
    const desktopEffectiveOffset = isAlreadyFullViewport ? DEFAULT_DESKTOP_OFFSET : measuredMargin

    // Calculate Chakra margin units from pixel values (4px per unit)
    const getChakraMarginUnit = (pixels) => {
        if (pixels <= 0) return 0
        return -Math.round(pixels / 4)
    }

    return {
        // Ref to attach to the carousel container for measurement
        containerRef,

        // Whether to apply bleed at each breakpoint
        shouldBleedMobile,
        shouldBleedTablet,
        shouldBleedDesktop,

        // Whether carousel is scrollable (for arrow visibility)
        isScrollableMobile,
        isScrollableTablet,
        isScrollableDesktop,

        // Whether container is already full viewport width (no container providing margins)
        // Use this to determine if title needs padding
        isAlreadyFullViewport,

        // Offset values - use appropriate offset when bleeding
        // When already full viewport: use defaults; otherwise use measured margin
        mobileOffset: shouldBleedMobile ? mobileEffectiveOffset : 0,
        tabletOffset: shouldBleedTablet ? tabletEffectiveOffset : 0,
        desktopOffset: shouldBleedDesktop ? desktopEffectiveOffset : 0,

        // Chakra margin values (negative for bleed)
        // Apply negative margin when NOT already full viewport (need to break out of container)
        // When already full viewport, offset alone provides the edge padding without needing breakout
        bleedMargin: {
            base:
                shouldBleedMobile && !isAlreadyFullViewport
                    ? getChakraMarginUnit(mobileEffectiveOffset)
                    : 0,
            md:
                shouldBleedTablet && !isAlreadyFullViewport
                    ? getChakraMarginUnit(tabletEffectiveOffset)
                    : 0,
            lg:
                shouldBleedDesktop && !isAlreadyFullViewport
                    ? getChakraMarginUnit(desktopEffectiveOffset)
                    : 0
        },

        // Raw bleed conditions before isAlreadyFullViewport check (for debugging)
        isFullWidth,

        // Viewport width for calculating adjusted slidesPerView when isAlreadyFullViewport
        viewportWidth
    }
}

export default useCarouselBleed
